import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { toast } from 'react-hot-toast';
import PremiumImageUploader from '../../components/PremiumImageUploader';

// import MfaSettings from '../../components/MfaSettings'; // DISABLED: 2FA not working

// Footer Component
const Footer = () => (
  <div className="mt-8 pt-6 text-center text-sm text-gray-400 dark:text-gray-600">
    Powered by{" "}
    <a
      href="https://klorah.com"
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
    >
      Klorah
    </a>
  </div>
);

const ProfileEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    username: '',
    professionalTitle: '',
    bio: '',
    profilePicture: '',
    services: [],
    portfolio: [],
    contactInfo: {
      email: '',
      phone: '',
      location: ''
    }
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!auth.currentUser) {
        navigate('/');
        return;
      }

      try {
        const profileRef = doc(db, 'profiles', auth.currentUser.uid);
        const profileSnap = await getDoc(profileRef);
        
        if (profileSnap.exists()) {
          setProfile(profileSnap.data());
        } else {
          navigate('/app/profile/welcome');
          return;
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error('Error loading profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);





  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!auth.currentUser) {
      toast.error('Please sign in to update your profile');
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, 'profiles', auth.currentUser.uid), {
        ...profile,
        username: profile.username.toLowerCase(),
        updatedAt: new Date().toISOString()
      });
      
      toast.success('Profile updated successfully!');
      navigate('/profile');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-full bg-white dark:bg-black">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 dark:border-gray-700 border-t-gray-800 dark:border-t-gray-200"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-white dark:bg-black">
      <div className="max-w-3xl mx-auto px-6 py-8 pb-32">
        {/* Header */}
        <div className="mb-10">
          <button
            type="button"
            onClick={() => navigate('/app/profile')}
            className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Profile
          </button>
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white tracking-tight">Edit Profile</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">Update your professional information and settings</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info Section */}
          <section className="bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#222] rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Basic Information</h2>

            <div className="space-y-6">
              <div>
                <PremiumImageUploader
                  variant="profile"
                  currentImage={profile.profilePicture || ''}
                  onImageChange={(imageUrl) => {
                    setProfile(prev => ({ ...prev, profilePicture: imageUrl }));
                  }}
                  uploadPath="profilePictures"
                  label="Profile Picture"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Full Name</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 focus:border-gray-400 dark:focus:border-[#444] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Username</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">@</span>
                    <input
                      type="text"
                      value={profile.username}
                      onChange={(e) => setProfile(prev => ({ ...prev, username: e.target.value }))}
                      className="w-full pl-8 pr-4 py-3 bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 focus:border-gray-400 dark:focus:border-[#444] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                      placeholder="johndoe"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Professional Title</label>
                <input
                  type="text"
                  value={profile.professionalTitle}
                  onChange={(e) => setProfile(prev => ({ ...prev, professionalTitle: e.target.value }))}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 focus:border-gray-400 dark:focus:border-[#444] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                  placeholder="Photographer, Designer, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Bio</label>
                <textarea
                  value={profile.bio}
                  onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 focus:border-gray-400 dark:focus:border-[#444] h-28 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 resize-none transition-all"
                  placeholder="Tell clients about yourself..."
                />
              </div>
            </div>
          </section>

          {/* Contact Info Section */}
          <section className="bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#222] rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Contact Information</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Email</label>
                <input
                  type="email"
                  value={profile.contactInfo?.email || ''}
                  onChange={(e) => setProfile(prev => ({
                    ...prev,
                    contactInfo: { ...prev.contactInfo, email: e.target.value }
                  }))}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 focus:border-gray-400 dark:focus:border-[#444] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Phone</label>
                <input
                  type="tel"
                  value={profile.contactInfo?.phone || ''}
                  onChange={(e) => setProfile(prev => ({
                    ...prev,
                    contactInfo: { ...prev.contactInfo, phone: e.target.value }
                  }))}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 focus:border-gray-400 dark:focus:border-[#444] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                  placeholder="+1 (123) 456-7890"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Location</label>
                <input
                  type="text"
                  value={profile.contactInfo?.location || ''}
                  onChange={(e) => setProfile(prev => ({
                    ...prev,
                    contactInfo: { ...prev.contactInfo, location: e.target.value }
                  }))}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 focus:border-gray-400 dark:focus:border-[#444] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                  placeholder="City, Country"
                />
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section className="bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#222] rounded-2xl p-6 overflow-hidden" data-section="services">
            <div className="flex items-center justify-between mb-6 gap-4">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Services</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Add your service offerings</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const newService = {
                    name: '',
                    description: '',
                    price: '',
                    currency: 'USD',
                    image: ''
                  };
                  setProfile(prev => ({
                    ...prev,
                    services: [newService, ...(prev.services || [])]
                  }));
                }}
                className="px-4 py-2.5 text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                + Add Service
              </button>
            </div>

            <div className="space-y-4">
              {(profile.services || []).length === 0 && (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                  <p>No services added yet</p>
                </div>
              )}
              {(profile.services || []).map((service, index) => (
                <div key={index} className="bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl p-5 space-y-4">
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Service {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setProfile(prev => ({
                          ...prev,
                          services: prev.services.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-red-500 hover:text-red-600 text-sm font-medium transition-colors"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="space-y-4">
                    <PremiumImageUploader
                      variant="service"
                      currentImage={service.image || ''}
                      onImageChange={(imageUrl) => {
                        const newServices = [...profile.services];
                        newServices[index] = { ...newServices[index], image: imageUrl };
                        setProfile(prev => ({ ...prev, services: newServices }));
                      }}
                      uploadPath="services"
                      label="Image"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Service Name</label>
                        <input
                          type="text"
                          value={service.name || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 100) {
                              const newServices = [...profile.services];
                              newServices[index] = { ...newServices[index], name: value };
                              setProfile(prev => ({ ...prev, services: newServices }));
                            }
                          }}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                          placeholder="e.g., Wedding Photography"
                          maxLength={100}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Price</label>
                        <div className="flex">
                          <input
                            type="text"
                            value={service.price || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9.,]/g, '');
                              if (value.length <= 10) {
                                const newServices = [...profile.services];
                                newServices[index] = { ...newServices[index], price: value };
                                setProfile(prev => ({ ...prev, services: newServices }));
                              }
                            }}
                            className="flex-1 min-w-0 px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-l-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                            placeholder="500"
                            maxLength={10}
                          />
                          <select
                            value={service.currency || 'USD'}
                            onChange={(e) => {
                              const newServices = [...profile.services];
                              newServices[index] = { ...newServices[index], currency: e.target.value };
                              setProfile(prev => ({ ...prev, services: newServices }));
                            }}
                            className="w-[72px] flex-shrink-0 px-2 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-l-0 border-gray-200 dark:border-[#333] rounded-r-xl text-sm text-gray-900 dark:text-white focus:outline-none"
                          >
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                            <option value="CAD">CAD</option>
                            <option value="AUD">AUD</option>
                            <option value="BMD">BMD</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Description</label>
                      <textarea
                        value={service.description || ''}
                        onChange={(e) => {
                          const newServices = [...profile.services];
                          newServices[index] = { ...newServices[index], description: e.target.value };
                          setProfile(prev => ({ ...prev, services: newServices }));
                        }}
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 h-20 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 resize-none transition-all"
                        placeholder="Describe your service..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Portfolio Section */}
          <section className="bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#222] rounded-2xl p-6 overflow-hidden" data-section="portfolio">
            <div className="flex items-center justify-between mb-6 gap-4">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Portfolio</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Showcase your best work</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const newItem = {
                    title: '',
                    description: '',
                    image: '',
                    category: 'General'
                  };

                  setProfile(prev => {
                    let currentItems = [];
                    if (Array.isArray(prev.portfolio)) {
                      currentItems = prev.portfolio;
                    } else if (prev.portfolio?.items && Array.isArray(prev.portfolio.items)) {
                      currentItems = prev.portfolio.items;
                    }

                    if (prev.portfolio && !Array.isArray(prev.portfolio)) {
                      return {
                        ...prev,
                        portfolio: {
                          ...prev.portfolio,
                          items: [newItem, ...currentItems]
                        }
                      };
                    }

                    return {
                      ...prev,
                      portfolio: [newItem, ...currentItems]
                    };
                  });
                }}
                className="px-4 py-2.5 text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                + Add Item
              </button>
            </div>

            <div className="space-y-4">
              {(() => {
                let portfolioItems = [];
                if (Array.isArray(profile.portfolio)) {
                  portfolioItems = profile.portfolio;
                } else if (profile.portfolio?.items && Array.isArray(profile.portfolio.items)) {
                  portfolioItems = profile.portfolio.items;
                }

                if (portfolioItems.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                      <p>No portfolio items added yet</p>
                    </div>
                  );
                }

                return portfolioItems.map((item, index) => (
                  <div key={index} className="bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl p-5">
                    <div className="flex justify-between items-start mb-4">
                      <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Item {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setProfile(prev => {
                            let currentItems = [];
                            if (Array.isArray(prev.portfolio)) {
                              currentItems = prev.portfolio;
                            } else if (prev.portfolio?.items && Array.isArray(prev.portfolio.items)) {
                              currentItems = prev.portfolio.items;
                            }

                            const filteredItems = currentItems.filter((_, i) => i !== index);

                            if (prev.portfolio && !Array.isArray(prev.portfolio)) {
                              return {
                                ...prev,
                                portfolio: {
                                  ...prev.portfolio,
                                  items: filteredItems
                                }
                              };
                            }

                            return {
                              ...prev,
                              portfolio: filteredItems
                            };
                          });
                        }}
                        className="text-red-500 hover:text-red-600 text-sm font-medium transition-colors"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-5">
                      <div className="w-full sm:w-32 flex-shrink-0">
                        <PremiumImageUploader
                          variant="portfolio"
                          currentImage={item.image || ''}
                          onImageChange={(imageUrl) => {
                            setProfile(prev => {
                              let currentItems = [];
                              if (Array.isArray(prev.portfolio)) {
                                currentItems = [...prev.portfolio];
                              } else if (prev.portfolio?.items && Array.isArray(prev.portfolio.items)) {
                                currentItems = [...prev.portfolio.items];
                              }

                              currentItems[index] = { ...currentItems[index], image: imageUrl };

                              if (prev.portfolio && !Array.isArray(prev.portfolio)) {
                                return {
                                  ...prev,
                                  portfolio: {
                                    ...prev.portfolio,
                                    items: currentItems
                                  }
                                };
                              }

                              return {
                                ...prev,
                                portfolio: currentItems
                              };
                            });
                          }}
                          uploadPath="portfolio"
                          label="Image"
                        />
                      </div>

                      <div className="flex-1 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Title</label>
                            <input
                              type="text"
                              value={item.title || ''}
                              onChange={(e) => {
                                setProfile(prev => {
                                  let currentItems = [];
                                  if (Array.isArray(prev.portfolio)) {
                                    currentItems = [...prev.portfolio];
                                  } else if (prev.portfolio?.items && Array.isArray(prev.portfolio.items)) {
                                    currentItems = [...prev.portfolio.items];
                                  }
                                  currentItems[index] = { ...currentItems[index], title: e.target.value };
                                  if (prev.portfolio && !Array.isArray(prev.portfolio)) {
                                    return { ...prev, portfolio: { ...prev.portfolio, items: currentItems } };
                                  }
                                  return { ...prev, portfolio: currentItems };
                                });
                              }}
                              className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                              placeholder="e.g., Summer Wedding"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Category</label>
                            <select
                              value={item.category || 'General'}
                              onChange={(e) => {
                                setProfile(prev => {
                                  let currentItems = [];
                                  if (Array.isArray(prev.portfolio)) {
                                    currentItems = [...prev.portfolio];
                                  } else if (prev.portfolio?.items && Array.isArray(prev.portfolio.items)) {
                                    currentItems = [...prev.portfolio.items];
                                  }
                                  currentItems[index] = { ...currentItems[index], category: e.target.value };
                                  if (prev.portfolio && !Array.isArray(prev.portfolio)) {
                                    return { ...prev, portfolio: { ...prev.portfolio, items: currentItems } };
                                  }
                                  return { ...prev, portfolio: currentItems };
                                });
                              }}
                              className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none text-gray-900 dark:text-white transition-all"
                            >
                              <option value="Wedding">Wedding</option>
                              <option value="Portrait">Portrait</option>
                              <option value="Commercial">Commercial</option>
                              <option value="Event">Event</option>
                              <option value="General">General</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Description</label>
                          <textarea
                            value={item.description || ''}
                            onChange={(e) => {
                              setProfile(prev => {
                                let currentItems = [];
                                if (Array.isArray(prev.portfolio)) {
                                  currentItems = [...prev.portfolio];
                                } else if (prev.portfolio?.items && Array.isArray(prev.portfolio.items)) {
                                  currentItems = [...prev.portfolio.items];
                                }
                                currentItems[index] = { ...currentItems[index], description: e.target.value };
                                if (prev.portfolio && !Array.isArray(prev.portfolio)) {
                                  return { ...prev, portfolio: { ...prev.portfolio, items: currentItems } };
                                }
                                return { ...prev, portfolio: currentItems };
                              });
                            }}
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 h-16 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 resize-none transition-all"
                            placeholder="Describe this work..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </section>

          {/* Enhanced Features Toggle */}
          <section className="bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#222] rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Pro Features</h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Enhanced Portfolio</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Categories, layouts & metadata</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!profile.portfolio?.categories) {
                      setProfile(prev => {
                        let currentItems = [];
                        if (Array.isArray(prev.portfolio)) {
                          currentItems = prev.portfolio;
                        } else if (prev.portfolio?.items && Array.isArray(prev.portfolio.items)) {
                          currentItems = prev.portfolio.items;
                        }

                        return {
                          ...prev,
                          portfolio: {
                            items: currentItems,
                            categories: [
                              { name: 'Wedding', color: '#E6A15E' },
                              { name: 'Portrait', color: '#8B5CF6' },
                              { name: 'Commercial', color: '#059669' }
                            ],
                            settings: {
                              layout: 'masonry',
                              theme: 'modern',
                              showMetadata: true
                            }
                          }
                        };
                      });
                      toast.success('Enhanced portfolio enabled!');
                    }
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                    profile.portfolio?.categories
                      ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400'
                      : 'bg-[#FFB27D] text-black hover:bg-[#FFB27D]/90'
                  }`}
                >
                  {profile.portfolio?.categories ? 'Enabled' : 'Enable'}
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Service Packages</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Advanced pricing & features</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!profile.servicePackages) {
                      setProfile(prev => ({
                        ...prev,
                        servicePackages: []
                      }));
                      toast.success('Service packages enabled!');
                    }
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                    profile.servicePackages
                      ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400'
                      : 'bg-[#FFB27D] text-black hover:bg-[#FFB27D]/90'
                  }`}
                >
                  {profile.servicePackages ? 'Enabled' : 'Enable'}
                </button>
              </div>
            </div>
          </section>

          {/* Service Packages Section */}
          {profile.servicePackages && (
            <section className="bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#222] rounded-2xl p-6 overflow-hidden" data-section="servicePackages">
              <div className="flex items-center justify-between mb-6 gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Service Packages</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Create detailed service offerings</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newPackage = {
                      id: `pkg_${Date.now()}`,
                      name: '',
                      description: '',
                      basePrice: '',
                      currency: 'USD',
                      duration: '',
                      category: 'General',
                      includedServices: [''],
                      addOns: [],
                      image: '',
                      popular: false,
                      isActive: true
                    };
                    setProfile(prev => ({
                      ...prev,
                      servicePackages: [newPackage, ...(prev.servicePackages || [])]
                    }));
                  }}
                  className="px-4 py-2.5 text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex-shrink-0"
                >
                  + Add Package
                </button>
              </div>

              <div className="space-y-4">
                {(profile.servicePackages || []).length === 0 && (
                  <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                    <p>No packages added yet</p>
                  </div>
                )}
                {(profile.servicePackages || []).map((pkg, index) => (
                  <div key={pkg.id} className="bg-white dark:bg-[#0D0D0D] border border-gray-200 dark:border-[#333] rounded-xl p-5 space-y-5">
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Package {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setProfile(prev => ({
                            ...prev,
                            servicePackages: prev.servicePackages.filter((_, i) => i !== index)
                          }));
                        }}
                        className="text-red-500 hover:text-red-600 text-sm font-medium transition-colors"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="space-y-4">
                      <PremiumImageUploader
                        variant="service"
                        currentImage={pkg.image || ''}
                        onImageChange={(imageUrl) => {
                          const newPackages = [...profile.servicePackages];
                          newPackages[index] = { ...newPackages[index], image: imageUrl };
                          setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                        }}
                        uploadPath="servicePackages"
                        label="Package Image"
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Package Name</label>
                          <input
                            type="text"
                            value={pkg.name || ''}
                            onChange={(e) => {
                              const newPackages = [...profile.servicePackages];
                              newPackages[index] = { ...newPackages[index], name: e.target.value };
                              setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                            }}
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                            placeholder="e.g., Wedding Essential"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Category</label>
                          <select
                            value={pkg.category || 'General'}
                            onChange={(e) => {
                              const newPackages = [...profile.servicePackages];
                              newPackages[index] = { ...newPackages[index], category: e.target.value };
                              setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                            }}
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none text-gray-900 dark:text-white transition-all"
                          >
                            <option value="Wedding">Wedding</option>
                            <option value="Portrait">Portrait</option>
                            <option value="Commercial">Commercial</option>
                            <option value="Event">Event</option>
                            <option value="General">General</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Description</label>
                        <textarea
                          value={pkg.description || ''}
                          onChange={(e) => {
                            const newPackages = [...profile.servicePackages];
                            newPackages[index] = { ...newPackages[index], description: e.target.value };
                            setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                          }}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 h-16 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 resize-none transition-all"
                          placeholder="What's included..."
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Price</label>
                          <input
                            type="number"
                            value={pkg.basePrice || ''}
                            onChange={(e) => {
                              const newPackages = [...profile.servicePackages];
                              newPackages[index] = { ...newPackages[index], basePrice: parseFloat(e.target.value) || 0 };
                              setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                            }}
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                            placeholder="299"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Currency</label>
                          <select
                            value={pkg.currency || 'USD'}
                            onChange={(e) => {
                              const newPackages = [...profile.servicePackages];
                              newPackages[index] = { ...newPackages[index], currency: e.target.value };
                              setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                            }}
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none text-gray-900 dark:text-white transition-all"
                          >
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                            <option value="CAD">CAD</option>
                            <option value="AUD">AUD</option>
                            <option value="BMD">BMD</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Duration</label>
                          <input
                            type="text"
                            value={pkg.duration || ''}
                            onChange={(e) => {
                              const newPackages = [...profile.servicePackages];
                              newPackages[index] = { ...newPackages[index], duration: e.target.value };
                              setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                            }}
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all"
                            placeholder="e.g., 8 hours"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Included Services</label>
                        <div className="space-y-2">
                          {(pkg.includedServices || ['']).map((service, serviceIndex) => (
                            <div key={serviceIndex} className="flex gap-2">
                              <input
                                type="text"
                                value={service}
                                onChange={(e) => {
                                  const newPackages = [...profile.servicePackages];
                                  const newServices = [...(newPackages[index].includedServices || [''])];
                                  newServices[serviceIndex] = e.target.value;
                                  newPackages[index] = { ...newPackages[index], includedServices: newServices };
                                  setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                                }}
                                className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333] rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 text-sm transition-all"
                                placeholder="e.g., 300+ edited photos"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newPackages = [...profile.servicePackages];
                                  const newServices = newPackages[index].includedServices.filter((_, i) => i !== serviceIndex);
                                  newPackages[index] = { ...newPackages[index], includedServices: newServices };
                                  setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                                }}
                                className="px-3 text-red-500 hover:text-red-600 transition-colors"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const newPackages = [...profile.servicePackages];
                              const newServices = [...(newPackages[index].includedServices || ['']), ''];
                              newPackages[index] = { ...newPackages[index], includedServices: newServices };
                              setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                            }}
                            className="text-sm text-[#FFB27D] hover:text-[#FFB27D]/80 font-medium transition-colors"
                          >
                            + Add service
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 pt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pkg.popular || false}
                            onChange={(e) => {
                              const newPackages = [...profile.servicePackages];
                              newPackages[index] = { ...newPackages[index], popular: e.target.checked };
                              setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                            }}
                            className="rounded border-gray-300 dark:border-gray-600 text-[#FFB27D] focus:ring-[#FFB27D]"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">Popular</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pkg.isActive !== false}
                            onChange={(e) => {
                              const newPackages = [...profile.servicePackages];
                              newPackages[index] = { ...newPackages[index], isActive: e.target.checked };
                              setProfile(prev => ({ ...prev, servicePackages: newPackages }));
                            }}
                            className="rounded border-gray-300 dark:border-gray-600 text-[#FFB27D] focus:ring-[#FFB27D]"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">Active</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Sticky Save Bar */}
          <div className="sticky bottom-0 left-0 right-0 bg-white/80 dark:bg-black/80 backdrop-blur-lg border-t border-gray-200 dark:border-[#222] -mx-6 px-6 py-4 mt-8">
            <div className="flex justify-between items-center max-w-3xl mx-auto">
              <button
                type="button"
                onClick={() => navigate('/app/profile')}
                className="px-6 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 transition-all"
              >
                {saving ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white dark:border-gray-900 border-t-transparent"></div>
                    Saving...
                  </div>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>

          <Footer />
        </form>
      </div>
    </div>
  );
};

export default ProfileEditor;